import { fetchWithAuth, getAccessToken } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Authentication API service
 */
export const AuthApi = {
  /**
   * Login with credentials
   * @param {Object} credentials - Object containing email and password
   * @returns {Promise<Response>} Login response
   */
  login: async (credentials) => {
    console.log('AuthApi: Making login request with JSON data');
    console.log('AuthApi: Login credentials:', { email: credentials.email, password: '[REDACTED]' });
    
    try {
      const response = await fetch(`${DATA_SERVICE_BASE_URL}/api/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', // ✅ FIXED: Use JSON content type
          'User-Agent': 'WebClient/1.0',
          'X-Forwarded-For': '127.0.0.1'
        },
        body: JSON.stringify(credentials),
        credentials: 'include'
      });
      
      console.log('AuthApi: Login response status:', response.status);
      return response;
    } catch (error) {
      console.error('AuthApi: Login request failed:', error);
      throw error;
    }
  },

  /**
   * Logout the current user session
   * @param {string} sessionId - Session ID to invalidate
   * @returns {Promise<Response>} Logout response
   */
  logout: async (userId) => {
    console.log('AuthApi.logout called for user:', userId);
    
    const token = getAccessToken();
    try {
      // Plain fetch to avoid recursive logout logic
      const response = await fetch(`${DATA_SERVICE_BASE_URL}/api/users/logout/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      if (response.status === 401) {
        console.log('AuthApi.logout: Token already invalid (401)');
        return { ok: true };
      }
      return response;
    } catch (error) {
      console.error('AuthApi.logout: Network error:', error);
      return { ok: true };
    }
  },

  /**
   * ✅ VERIFIED: Refresh JWT access token using refresh token
   * @param {string} refreshToken - Refresh token to use
   * @returns {Promise<Response>} Refresh token response
   */
  refreshToken: async (refreshToken) => {
    console.log('AuthApi: Refreshing JWT token');
    try {
      const body = JSON.stringify({ refreshToken });
      const response = await fetch(`${DATA_SERVICE_BASE_URL}/api/users/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'WebClient/1.0',
          'X-Forwarded-For': '127.0.0.1'
          // NOTE: Intentionally no Authorization header
        },
        body,
        credentials: 'include' // ensure JSESSIONID flows
      });
      console.log('AuthApi: Refresh token response status:', response.status);
      if (!response.ok) {
        const txt = await response.text().catch(()=> '');
        console.error('AuthApi: Refresh token error response:', txt);
      }
      return response;
    } catch (e) {
      console.error('AuthApi: Refresh token request failed:', e);
      throw e;
    }
  },

  /**
   * Validate the current user's access token
   * @returns {Promise<Response>} User details response
   */
  validateToken: async () => {
    const token = getAccessToken();
    if (!token) throw new Error('NO_TOKEN');
    // Backend expects token as request parameter; also send Authorization header (fetchWithAuth adds it)
    const url = `${DATA_SERVICE_BASE_URL}/api/users/validate-token?token=${encodeURIComponent(token)}`;
    const resp = await fetchWithAuth(url, { method: 'GET' });
    return resp;
  },

  /**
   * Create service token
   * @param {number} userId - User ID
   * @param {number} durationInDays - Duration in days
   * @param {string} serviceDescription - Service description
   * @returns {Promise<Response>} Service token response
   */
  createServiceToken: async (userId, durationInDays, serviceDescription) => {
    const queryParams = new URLSearchParams({ userId, durationInDays, serviceDescription }).toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/create-service-token?${queryParams}`, {
      method: 'POST',
    });
  },

  /**
   * Request a password reset email
   * POST /api/users/request-reset?email={email}
   */
  requestPasswordReset: async (email) => {
    const url = `${DATA_SERVICE_BASE_URL}/api/users/request-reset?email=${encodeURIComponent(email)}`;
    try {
      return await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'WebClient/1.0',
          'X-Forwarded-For': '127.0.0.1'
        },
        credentials: 'include'
      });
    } catch (e) {
      console.error('AuthApi.requestPasswordReset failed:', e);
      throw e;
    }
  },
};